// frontend/src/pages/user/P2PPage.tsx
import { useState } from 'react'
import { Sparkles, ChevronDown } from 'lucide-react'
import { formatUnits } from 'viem'
import { Link } from 'react-router-dom'
import { OrderForm } from '../../components/p2p/OrderForm'
import { OrderBookTabs } from '../../components/p2p/OrderBookTabs'
import { useP2PStats } from '../../hooks/useP2PStats'
import { useP2POrderbook } from '../../hooks/useP2POrderbook'
import { useP2PPlatformFees } from '../../hooks/useP2PPlatformFees'
import { P2P_PAIR } from '../../lib/p2p/tokens'
import { cn } from '../../lib/utils'

export function P2PPage() {
  const stats = useP2PStats()
  const { connected } = useP2POrderbook(P2P_PAIR)

  return (
    <div className="relative pb-16 overflow-x-clip">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 flex justify-center">
        <div className="h-[420px] w-[640px] max-w-full rounded-full bg-uni-500/10 blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <header className="text-center space-y-3 pt-2 pb-6 sm:pb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-uni-500/30 bg-uni-500/5 text-uni-400 text-[11px] font-mono uppercase tracking-widest">
            <Sparkles className="w-3 h-3" />
            Mercado P2P · cross-chain
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight">
            <span className="text-uni-500">w2usdt</span> ↔ <span className="text-uni-500">USDT</span>
          </h1>
          <p className="max-w-2xl mx-auto text-sm text-dark-400">
            <strong className="text-white">¿Para qué sirve?</strong> Para cambiar tus w2usdt por USDT (o al revés) directamente con otra persona, sin pasar por un exchange.{' '}
            <strong className="text-white">¿Cómo funciona?</strong> Publicas tu orden y bloqueas tus tokens; cuando alguien la acepte, recibes lo tuyo en la otra red automáticamente. Solo pagas el gas de tu red.
          </p>
          <PlatformFeeNote />
          <StatsBar stats={stats.data} connected={connected} />
        </header>

        {/* Two columns: form sidebar + orderbook main */}
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 sm:gap-6">
          {/* Sidebar — Create order */}
          <aside className="lg:sticky lg:top-20 lg:self-start">
            <div className="relative">
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-uni-500/25 via-uni-600/5 to-transparent blur-sm opacity-70" />
              <div className="relative rounded-2xl border border-dark-800 bg-dark-950/85 backdrop-blur-xl">
                <div className="px-4 sm:px-5 pt-4 pb-3 border-b border-dark-800">
                  <h2 className="font-display text-lg font-semibold text-white">Publicar orden</h2>
                  <p className="text-[11px] font-mono text-dark-500 uppercase tracking-wider mt-0.5">
                    bloquea tu lado, espera contraparte
                  </p>
                </div>
                <div className="p-4 sm:p-5">
                  <OrderForm />
                </div>
              </div>
            </div>
          </aside>

          {/* Main — Order book */}
          <main className="min-w-0">
            <OrderBookTabs />

            {/* Footer shortcuts */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-sm">
              <Link
                to="/p2p/orders"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-dark-800 bg-dark-900/60 hover:border-uni-500/40 hover:text-uni-400 text-dark-300 transition-colors"
              >
                Mis órdenes →
              </Link>
              <Link
                to="/p2p/trades"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-dark-800 bg-dark-900/60 hover:border-uni-500/40 hover:text-uni-400 text-dark-300 transition-colors"
              >
                Mis trades →
              </Link>
            </div>
          </main>
        </div>

        {/* FAQ */}
        <FAQ />
      </div>
    </div>
  )
}

const FAQ_ITEMS: { q: string; a: React.ReactNode }[] = [
  {
    q: '¿Qué es este mercado P2P?',
    a: (
      <>
        Un orderbook cross-chain entre <strong>w2usdt</strong> (Uni2 chain) y <strong>USDT</strong> (BSC). Los tokens no se mueven al treasury de nadie:
        cada orden bloquea tus tokens en un contrato de escrow on-chain en TU chain, y se libera atómicamente solo cuando un matching engine
        encuentra una contraparte compatible al otro lado.
      </>
    ),
  },
  {
    q: '¿Cómo se ejecuta un intercambio?',
    a: (
      <>
        Publicas una orden indicando qué token vendes (w2usdt o USDT), cuánto, una <strong>prima</strong> de –10% a +10% y un plazo de expiración.
        El motor compara tu orden contra las opuestas y, cuando hay match, el relayer firma <code>executeTrades</code> en BSC y en Uni2.
        Los tokens del maker viajan a tu wallet y los tuyos al maker — los dos lados en la misma ronda.
      </>
    ),
  },
  {
    q: '¿Quién paga el gas?',
    a: (
      <>
        Tú pagas <strong>solo el gas en TU chain de origen</strong>: <code>approve</code> + <code>createOrder</code> al publicar, y
        <code> cancelOrder</code> si decides retirar antes de la expiración.
        <br />
        El <strong>relayer del backend paga el gas del settlement en AMBAS chains</strong>, y también el gas del <code>expireOrder</code> si tu orden caduca.
        <br />
        Si vendes USDT necesitas <strong>BNB</strong> en tu wallet para gas. Si vendes w2usdt necesitas <strong>UNI2</strong>. La chain destino la cubre el relayer.
      </>
    ),
  },
  {
    q: '¿Para qué sirve la prima?',
    a: (
      <>
        Es tu margen sobre la paridad 1:1. <strong className="text-emerald-300">+1%</strong> = cobras un 1% de comisión, recibes más a cambio.{' '}
        <strong className="text-rose-300">−1%</strong> = das descuento, recibes menos pero tu orden es más atractiva. <strong>0%</strong> = trueque exacto.
        El contrato la limita a ±10%.
      </>
    ),
  },
  {
    q: '¿Qué pasa si nadie toma mi orden?',
    a: (
      <>
        Tus tokens quedan en el escrow hasta que llegue <code>expiresAt</code>. Pasado ese momento, el cron del relayer ejecuta
        <code> expireOrder</code> y te devuelve tus tokens automáticamente — <strong>el relayer paga ese gas</strong>, tú no haces nada.
        Si quieres recuperarlos antes, cancelas manualmente desde "Mis órdenes" (sí pagas el gas de esa cancelación).
      </>
    ),
  },
  {
    q: '¿"Parcial OK" o "Todo o nada"?',
    a: (
      <>
        <strong>Parcial OK</strong>: otras órdenes pueden comer trozos de la tuya. Más probabilidad de ejecución, pero recibes en partes.
        <br />
        <strong>Todo o nada (AON)</strong>: solo se ejecuta si alguien la toma entera. Útil para órdenes grandes que no quieres fraccionar.
      </>
    ),
  },
  {
    q: '¿Es seguro? ¿Quién custodia los tokens?',
    a: (
      <>
        El escrow es el contrato <code>P2PEscrowV2</code> desplegado en cada chain (BSC y Uni2chain) con sus propias
        direcciones — las podés ver en el panel de admin o en el explorer. Solo el creador de una orden (msg.sender)
        puede cancelarla. El relayer tiene poder limitado: solo puede ejecutar matches válidos producidos por el motor
        o expirar órdenes vencidas — nunca puede mover tus fondos a otro lado.
      </>
    ),
  },
  {
    q: '¿Por qué a veces aparece un 502 en consola?',
    a: (
      <>
        El RPC público upstream nos pone rate limit ocasional bajo ráfagas. wagmi reintenta automáticamente y el flujo
        funciona — son ruido de consola, no errores reales del intercambio.
      </>
    ),
  },
]

function FAQ() {
  const [openIdx, setOpenIdx] = useState<number | null>(0)
  return (
    <section className="mt-10 sm:mt-16 max-w-3xl mx-auto">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-uni-500/30 bg-uni-500/5 text-uni-400 text-[11px] font-mono uppercase tracking-widest">
          FAQ
        </div>
        <h2 className="font-display text-xl sm:text-2xl font-semibold text-white mt-2">
          Cómo funciona el P2P
        </h2>
      </div>
      <div className="space-y-2">
        {FAQ_ITEMS.map((item, i) => {
          const isOpen = openIdx === i
          return (
            <div
              key={i}
              className={cn(
                'rounded-xl border bg-dark-950/60 backdrop-blur overflow-hidden transition-colors',
                isOpen ? 'border-uni-500/40' : 'border-dark-800 hover:border-dark-700',
              )}
            >
              <button
                type="button"
                onClick={() => setOpenIdx(isOpen ? null : i)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
              >
                <span className="text-sm font-medium text-white">{item.q}</span>
                <ChevronDown
                  className={cn(
                    'w-4 h-4 text-dark-400 shrink-0 transition-transform',
                    isOpen && 'rotate-180 text-uni-400',
                  )}
                />
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pt-1 text-sm text-dark-300 leading-relaxed border-t border-dark-800/60">
                  {item.a}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}

function StatsBar({
  stats,
  connected,
}: {
  stats: { volumeW2usdt: string; volumeUsdt: string; tradesCount: number } | undefined
  connected: boolean
}) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
      <Stat label="Vol 24h w2usdt" value={stats ? humanAmount(stats.volumeW2usdt) : '—'} />
      <Stat label="Vol 24h USDT" value={stats ? humanAmount(stats.volumeUsdt) : '—'} />
      <Stat label="Trades 24h" value={stats ? String(stats.tradesCount) : '—'} />
      <LiveIndicator connected={connected} />
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-3 py-1.5 rounded-full border border-dark-800 bg-dark-900/60 text-xs">
      <span className="text-dark-500 mr-1.5">{label}</span>
      <span className="font-mono text-dark-100">{value}</span>
    </div>
  )
}

function LiveIndicator({ connected }: { connected: boolean }) {
  return (
    <div
      className={cn(
        'px-3 py-1.5 rounded-full border text-xs flex items-center gap-1.5 font-mono',
        connected
          ? 'border-uni-500/40 bg-uni-500/10 text-uni-400'
          : 'border-amber-500/30 bg-amber-500/5 text-amber-400',
      )}
    >
      <span
        className={cn(
          'w-1.5 h-1.5 rounded-full',
          connected ? 'bg-uni-500 animate-pulse' : 'bg-amber-400',
        )}
      />
      {connected ? 'LIVE' : 'OFFLINE'}
    </div>
  )
}

function PlatformFeeNote() {
  const { uni2, bsc, isLoading } = useP2PPlatformFees()
  if (isLoading || (!uni2 && !bsc)) return null

  const fmtPct = (bps: number) => `${(bps / 100).toFixed(2)}%`
  const uni2Pct = uni2 ? fmtPct(uni2.platformFeeBps) : null
  const bscPct = bsc ? fmtPct(bsc.platformFeeBps) : null
  // If both chains charge the same, collapse into one number — that's the
  // usual case and reads better than "0.50% (Uni2) / 0.50% (BSC)".
  const collapsed = uni2Pct && bscPct && uni2Pct === bscPct ? uni2Pct : null

  return (
    <p className="max-w-2xl mx-auto text-[11px] font-mono text-dark-500 mt-2">
      Comisión de plataforma{' '}
      {collapsed ? (
        <span className="text-dark-300">{collapsed}</span>
      ) : (
        <>
          <span className="text-dark-300">{uni2Pct ?? '—'}</span> en Uni2 ·{' '}
          <span className="text-dark-300">{bscPct ?? '—'}</span> en BSC
        </>
      )}
      {' '}sobre cada intercambio (cubre la gestión y el gas del smart contract).
    </p>
  )
}

function humanAmount(wei: string, decimals = 18, maxFrac = 2): string {
  try {
    const formatted = formatUnits(BigInt(wei), decimals)
    const [w, f = ''] = formatted.split('.')
    const grouped = w.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    if (maxFrac === 0) return grouped
    const frac = f.slice(0, maxFrac).padEnd(maxFrac, '0').replace(/0+$/, '')
    return frac ? `${grouped}.${frac}` : grouped
  } catch {
    return '0'
  }
}
